import { useState, useCallback } from 'react';
import { useSupabaseStore } from '@/pages/CustomerSupport/supabaseStore';
import toast from 'react-hot-toast';
import { useTranslations } from 'use-intl';

export interface InstructionVersion {
	id: string;
	entity_type: string;
	entity_id: string;
	version_number: number;
	content: Record<string, unknown>;
	created_at: string;
}

export function useInstructionHistory() {
	const supabase = useSupabaseStore((state) => state.supabase);
	const t = useTranslations('VersionHistory');
	const [versions, setVersions] = useState<InstructionVersion[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const loadHistory = useCallback(
		async (entityId: string) => {
			if (!entityId) {
				setVersions([]);
				return;
			}

			setIsLoading(true);
			try {
				const { data, error } = await supabase
					.from('instruction_version_history')
					.select('*')
					.eq('entity_id', entityId)
					.order('version_number', { ascending: false });

				if (error) {
					setVersions([]);
					return;
				}

				setVersions((data as InstructionVersion[]) ?? []);
			} finally {
				setIsLoading(false);
			}
		},
		[supabase]
	);

	const copyVersionContent = useCallback(
		(content: Record<string, unknown>, contentKey: string) => {
			const text = content[contentKey];
			const textToCopy = typeof text === 'string' ? text : JSON.stringify(text ?? '');

			navigator.clipboard
				.writeText(textToCopy)
				.then(() => {
					toast.success(t('CopyVersion') || 'Copied to clipboard', { duration: 3000 });
				})
				.catch(() => {
					toast.error(t('CopyFailed') || 'Failed to copy');
				});
		},
		[t]
	);

	return { versions, isLoading, loadHistory, copyVersionContent };
}
